/**
 * Single source of truth for the PDP: selected colour, order quantity and its split
 * across sizes.
 *
 * Every derived surface (gallery, price info, quantity tiers, size distribution, the
 * offer button's state) is re-rendered from that state, so they can never disagree.
 * Prices come from the embedded variant JSON rather than a section re-fetch, which
 * keeps the Swiper instances alive between changes.
 */
if (!customElements.get('main-product')) {
  customElements.define(
    'main-product',
    class MainProduct extends HTMLElement {
      connectedCallback() {
        this.variants = this.parseVariants();
        if (!this.variants.length) return;

        this.colorPosition = Number(this.dataset.colorOptionPosition) || null;
        this.sizePosition = Number(this.dataset.sizeOptionPosition) || null;
        this.moneyFormat = this.dataset.moneyFormat || '{{ amount_with_comma_separator }} €';

        this.variant =
          this.findVariantFromInputs() || this.variants.find((variant) => variant.available) || this.variants[0];
        this.quantity = this.minimumQuantity(this.variant);
        this.customMode = false;

        this.addEventListener('change', this.onOptionChange);

        const tiers = this.querySelector('[data-quantity-tiers]');
        tiers?.addEventListener('click', this.onTierClick);
        tiers?.addEventListener('click', this.onCustomRowClick);
        tiers?.addEventListener('input', this.onCustomInput);
        this.customQuantityInput?.addEventListener('focus', this.onCustomFocus);

        this.sizePicker = this.sizePosition ? this.querySelector('[data-size-picker]') : null;
        /** @type {Set<string>} Sizes the shopper picked, whether or not they carry pieces yet. */
        this.selectedSizes = new Set();
        /** @type {Map<string, number>} Pieces per size; only sizes with at least one piece. */
        this.sizeQuantities = new Map();
        if (this.sizePicker) this.initSizePicker();

        this.render();
        this.announceVariant();
      }

      /**
       * The Printbox "Customize" app block reads the variant it opens the editor with
       * from a document-level `variant:update` (Horizon convention); this section has
       * no `/cart/add` form it could fall back to. A null resource disables its button.
       */
      announceVariant() {
        document.dispatchEvent(new CustomEvent('variant:update', { detail: { resource: this.variant } }));
      }

      parseVariants() {
        try {
          return JSON.parse(this.querySelector('[data-product-variants]').textContent) ?? [];
        } catch {
          return [];
        }
      }

      /* ---------- state ---------- */

      onOptionChange = (event) => {
        if (!event.target.matches('[data-option-position]')) return;

        const variant = this.findVariantFromInputs();

        // The chosen colour does not exist as a variant at all. Keep the picker on the
        // shopper's choice and mark the product unquotable rather than silently
        // snapping back to the previous variant.
        if (!variant) {
          this.variant = null;
          this.setUnavailable();
          this.announceVariant();
          return;
        }

        const colorChanged = this.colorOf(variant) !== this.colorOf(this.variant);
        this.variant = variant;
        this.announceVariant();

        // Rules and price breaks are per-variant. A custom quantity that the new
        // variant's rule rejects drops back to a preset rather than being silently
        // rounded to something the shopper never typed.
        if (this.customMode && this.validateCustomQuantity(this.customQuantityInput?.value).error) {
          this.customMode = false;
          this.showCustomError(null);
          if (this.customQuantityInput) this.customQuantityInput.value = '';
        }

        if (!this.customMode) this.quantity = this.nearestTier(this.quantity, variant);

        this.render({ syncGallery: colorChanged });
      };

      onTierClick = (event) => {
        const tier = event.target.closest('[data-tier-quantity]');
        if (!tier) return;

        this.customMode = false;
        this.quantity = Number(tier.dataset.tierQuantity);
        this.render();
      };

      /**
       * Focusing the field enters custom mode: the shopper is now typing their own
       * quantity, so the preset selection yields to it immediately. The value is
       * validated on the way in, so an empty or invalid field enters the mode but
       * keeps the offer blocked until it is fixed.
       */
      onCustomFocus = () => {
        if (!this.variant || this.customMode) return;

        this.customMode = true;

        // An empty field on entry is not yet an error — the shopper has only just
        // started. The message appears once they type something invalid.
        const raw = this.customQuantityInput?.value ?? '';
        const { value, error } = this.validateCustomQuantity(raw);
        this.showCustomError(raw.trim() === '' ? null : error);
        if (!error) this.quantity = value;

        this.render();
      };

      /**
       * The row's tick is now only a way *out* of custom mode — entering it is the
       * field's focus. Clicking the row (but not the tick) still just focuses the
       * field so tapping anywhere on it starts typing.
       */
      onCustomRowClick = (event) => {
        const row = event.target.closest('[data-custom-row]');
        if (!row || !this.variant) return;

        const confirm = event.target.closest('[data-custom-toggle]');
        if (!confirm || !this.customMode) {
          this.customQuantityInput?.focus();
          return;
        }

        this.customMode = false;
        this.showCustomError(null);
        this.quantity = this.nearestTier(this.quantity, this.variant);
        this.render();
      };

      /** Live feedback while typing; never rewrites the field under the caret. */
      onCustomInput = (event) => {
        if (!event.target.matches('[data-custom-quantity]')) return;
        if (!this.variant) return;

        const { value, error } = this.validateCustomQuantity(event.target.value);
        this.showCustomError(error);

        if (this.customMode && !error) {
          this.quantity = value;
          this.renderPriceInfo();
          this.syncTierStates();
          this.renderSizeDistribution();
        }

        this.renderOfferState();
      };

      /**
       * Validates against the selected variant's quantity rule.
       * @returns {{value: number|null, error: string|null}}
       */
      validateCustomQuantity(raw) {
        const { minEmpty, minInteger, minBelow, minStep, minAbove } = window.mainProductStrings;
        const { min = 1, max = null, increment = 1 } = this.variant.quantityRule ?? {};

        const text = String(raw ?? '').trim();
        if (text === '') return { value: null, error: minEmpty };

        // Letters, decimals and signs each get the accurate message rather than
        // being lumped in with "below the minimum".
        if (!/^\d+$/.test(text)) return { value: null, error: minInteger };

        const value = Number(text);
        if (value < min) return { value: null, error: minBelow.replace('[min]', min) };
        if (max !== null && value > max) return { value: null, error: minAbove.replace('[max]', max) };
        if ((value - min) % increment !== 0) return { value: null, error: minStep.replace('[step]', increment) };

        return { value, error: null };
      }

      showCustomError(message) {
        const error = this.querySelector('[data-custom-error]');
        if (!error) return;

        error.textContent = message ?? '';
        error.hidden = !message;

        this.customQuantityInput?.setAttribute('aria-invalid', message ? 'true' : 'false');
        this.querySelector('[data-custom-row]')?.classList.toggle('main-product__tier--invalid', Boolean(message));
      }

      get customQuantityValid() {
        return this.customMode && !this.validateCustomQuantity(this.customQuantityInput?.value).error;
      }

      selectedOptions() {
        const options = [];
        this.querySelectorAll('[data-option-position]:checked').forEach((input) => {
          options[Number(input.dataset.optionPosition) - 1] = input.value;
        });
        return options;
      }

      /**
       * Sizes are no longer picked one at a time, so a colour matches several variants.
       * The first buyable one stands in for pricing, availability and the gallery.
       */
      findVariantFromInputs() {
        const selected = this.selectedOptions();
        if (!selected.length) return null;

        const matches = this.variants.filter((variant) =>
          selected.every((value, index) => value === undefined || variant.options[index] === value)
        );

        return matches.find((variant) => variant.available) ?? matches[0];
      }

      colorOf(variant) {
        if (!this.colorPosition || !variant) return null;
        return variant.options[this.colorPosition - 1];
      }

      /** Read from the swatches rather than the variant, which is null for a missing combination. */
      get selectedColor() {
        if (!this.colorPosition) return null;
        return this.selectedOptions()[this.colorPosition - 1] ?? null;
      }

      minimumQuantity(variant) {
        return variant.quantityRule?.min ?? 1;
      }

      get customQuantityInput() {
        return this.querySelector('[data-custom-quantity]');
      }

      /** Highest preset tier the quantity qualifies for; used when leaving custom mode. */
      nearestTier(quantity, variant) {
        const tiers = this.tierQuantities(variant);

        // Without volume tiers there are no preset rows at all, so there is nothing
        // to snap to and the variant's own minimum stands.
        if (!tiers.length) return this.minimumQuantity(variant);

        const qualifying = tiers.filter((tier) => tier <= quantity);
        return qualifying.length ? qualifying[qualifying.length - 1] : tiers[0];
      }

      /* ---------- size distribution ---------- */

      initSizePicker() {
        this.sizePicker.addEventListener('click', this.onSizeClick);
        this.sizePicker.addEventListener('input', this.onSizeInput);
        this.sizePicker.querySelector('[data-size-adjust-button]')?.addEventListener('click', this.onSizeAdjust);
      }

      /**
       * Picking a size opens the panel and puts the caret in its row. Picking it again
       * removes it together with its pieces, so the progress never counts pieces for a
       * size the shopper dropped.
       */
      onSizeClick = (event) => {
        const button = event.target.closest('[data-size-value]');
        if (!button || button.getAttribute('aria-disabled') === 'true') return;

        const size = button.dataset.sizeValue;

        if (this.selectedSizes.has(size)) {
          this.removeSize(size);
          this.renderSizeDistribution();
          this.renderOfferState();
          return;
        }

        this.selectedSizes.add(size);
        this.renderSizeDistribution();
        this.sizeInput(size)?.focus();
      };

      onSizeInput = (event) => {
        const input = event.target.closest('[data-size-quantity]');
        if (!input) return;

        // A share is a whole number of pieces; anything else is dropped as it is typed
        // rather than flagged, since there is no other valid form to correct towards.
        const digits = input.value.replace(/\D/g, '');
        if (digits !== input.value) input.value = digits;

        const size = input.dataset.sizeQuantity;
        const quantity = Number(digits) || 0;

        // Typing into a row that was never picked picks it, so the buttons always
        // mirror where the pieces are. Emptying a row keeps it picked: the shopper
        // may be about to retype the number.
        if (quantity > 0) {
          this.sizeQuantities.set(size, quantity);
          this.selectedSizes.add(size);
        } else {
          this.sizeQuantities.delete(size);
        }

        this.renderSizeDistribution();
        this.renderOfferState();
      };

      /**
       * Adopts the distributed total as the order quantity. It lands on a preset tier
       * when one matches, otherwise in the custom field, where the variant's quantity
       * rule still applies.
       */
      onSizeAdjust = () => {
        const assigned = this.assignedQuantity;
        if (!assigned || !this.variant) return;

        if (this.tierQuantities(this.variant).includes(assigned)) {
          this.customMode = false;
          this.showCustomError(null);
          this.quantity = assigned;
          this.render();
          return;
        }

        if (this.customQuantityInput) this.customQuantityInput.value = String(assigned);
        this.customMode = true;

        const { error } = this.validateCustomQuantity(assigned);
        this.showCustomError(error);
        if (!error) this.quantity = assigned;

        this.render();
      };

      removeSize(size) {
        this.selectedSizes.delete(size);
        this.sizeQuantities.delete(size);

        const input = this.sizeInput(size);
        if (input) input.value = '';
      }

      sizeInput(size) {
        return [...this.sizePicker.querySelectorAll('[data-size-quantity]')].find(
          (input) => input.dataset.sizeQuantity === size
        );
      }

      /** Size values in the order the picker lists them. */
      get sizeValues() {
        return [...this.sizePicker.querySelectorAll('[data-size-value]')].map((button) => button.dataset.sizeValue);
      }

      /** Size from the picker; colour and every other option (e.g. Print Area) from the radios. */
      variantForSize(size) {
        const selected = this.selectedOptions();
        selected[this.sizePosition - 1] = size;

        return this.variants.find((variant) =>
          selected.every((value, index) => value === undefined || variant.options[index] === value)
        );
      }

      /**
       * 30.10.2026 Pack-Schritte je Größe (Lusini-Gläser): jede Größe ist eine eigene Lieferanten-Variante mit eigener
       * Packgröße, also muss jede Größe für sich in ganzen Packs bestellt werden – nicht nur die Gesamtmenge.
       * @returns {Array<{size: string, step: number, quantity: number}>}
       */
      packErrors() {
        const errors = [];
        this.sizeQuantities.forEach((quantity, size) => {
          const step = this.variantForSize(size)?.quantityRule?.increment ?? 1;
          if (step > 1 && quantity % step !== 0) errors.push({ size, step, quantity });
        });
        return errors;
      }

      get assignedQuantity() {
        return [...this.sizeQuantities.values()].reduce((total, quantity) => total + quantity, 0);
      }

      /** How the pieces spread across the sizes compare with the order quantity. */
      get distributionState() {
        const assigned = this.assignedQuantity;
        if (assigned === this.quantity) return 'complete';
        return assigned > this.quantity ? 'over' : 'under';
      }

      /* ---------- offer ---------- */

      /**
       * The offer button is the only way forward from this page, so it carries every
       * reason the configuration cannot be quoted yet: no quotable variant, an invalid
       * custom quantity, or pieces not (or over-) assigned to sizes.
       */
      get canRequestOffer() {
        if (!this.variant?.available) return false;
        if (this.customMode && !this.customQuantityValid) return false;
        if (this.sizePicker && this.packErrors().length) return false;
        return !this.sizePicker || this.distributionState === 'complete';
      }

      /**
       * The lines the offer button puts on the quote list.
       *
       * With a size picker that is one line per size, all tagged with one group (product
       * + colour). The quote list prices the group at the tier its combined quantity
       * reaches — 20 + 30 pieces are a 50-piece order — and holds that total, not each
       * size, to the minimum.
       *
       * @returns {Array<{variant: object, quantity: number, min: number, priceBreaks: Array<{minimumQuantity: number, price: number}>, group?: string, groupMin?: number}>}
       */
      offerSelections() {
        if (!this.canRequestOffer) return [];

        if (!this.sizePicker) {
          const min = this.minimumQuantity(this.variant);
          return [
            {
              variant: this.variant,
              quantity: Math.max(this.quantity, min),
              min,
              priceBreaks: this.variant.priceBreaks ?? [],
            },
          ];
        }

        const otherOptions = this.selectedOptions()
          .filter((value, index) => value !== undefined && index !== this.colorPosition - 1)
          .join('/');
        const group = `${this.dataset.productId}:${this.selectedColor ?? ''}${otherOptions ? `:${otherOptions}` : ''}`;
        const groupMin = this.minimumQuantity(this.variant);

        return this.sizeValues
          .filter((size) => this.sizeQuantities.has(size))
          .map((size) => ({ variant: this.variantForSize(size), quantity: this.sizeQuantities.get(size) }))
          .filter(({ variant }) => variant)
          .map(({ variant, quantity }) => ({
            variant,
            quantity,
            min: 1,
            priceBreaks: variant.priceBreaks ?? [],
            group,
            groupMin,
          }));
      }

      /* ---------- pricing ---------- */

      /** Unit price for `quantity`, honouring the variant's volume tiers. */
      unitPrice(variant, quantity) {
        const applicable = (variant.priceBreaks ?? [])
          .filter((each) => quantity >= each.minimumQuantity)
          .sort((a, b) => b.minimumQuantity - a.minimumQuantity)[0];

        return applicable ? applicable.price : variant.price;
      }

      /** Discount of the tier's unit price against the variant's undiscounted price. */
      discountPercent(variant, quantity) {
        const base = variant.price;
        if (!base) return 0;
        return Math.round(((base - this.unitPrice(variant, quantity)) / base) * 100);
      }

      /**
       * Tier quantities this variant offers. Read from the Liquid-rendered rows so
       * the controller can never disagree with what is actually on the page.
       */
      tierQuantities(variant) {
        const min = this.minimumQuantity(variant);
        return [...this.querySelectorAll('[data-tier-quantity]')]
          .map((tier) => Number(tier.dataset.tierQuantity))
          .filter((quantity) => quantity >= min)
          .sort((a, b) => a - b);
      }

      /* ---------- money ---------- */

      formatMoney(cents) {
        const value = cents / 100;
        const usesComma = /amount_with_comma_separator|amount_no_decimals_with_comma_separator/.test(this.moneyFormat);
        const noDecimals = /no_decimals/.test(this.moneyFormat);

        const formatted = new Intl.NumberFormat(usesComma ? 'de-DE' : 'en-US', {
          minimumFractionDigits: noDecimals ? 0 : 2,
          maximumFractionDigits: noDecimals ? 0 : 2,
        }).format(value);

        return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, formatted);
      }

      /* ---------- render ---------- */

      render({ syncGallery = false } = {}) {
        // Every other render path dereferences the variant; the unavailable state is
        // painted by setUnavailable() instead.
        if (!this.variant) return;

        this.renderVariantMeta();
        this.renderOptionAvailability();
        this.renderSizeAvailability();
        this.syncTierStates();
        this.renderPriceInfo();
        this.renderSizeDistribution();
        this.renderOfferState();
        if (syncGallery) this.slideToColor();

        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.dataset.sectionId, variant: this.variant },
        });
      }

      /**
       * Marks each colour that cannot be bought. Availability is combinatorial — Navy
       * may be sold out in every size while White is not — so it is recomputed from the
       * variant data on every change.
       */
      renderOptionAvailability() {
        const selected = this.selectedOptions();

        this.querySelectorAll('[data-option-position]').forEach((input) => {
          const position = Number(input.dataset.optionPosition);

          // Hold every other option at its current choice and vary only this one.
          const candidate = [...selected];
          candidate[position - 1] = input.value;

          const buyable = this.variants.some(
            (variant) =>
              variant.available &&
              candidate.every((value, index) => value === undefined || variant.options[index] === value)
          );

          input.closest('label')?.classList.toggle('main-product__option--unavailable', !buyable);
          input.setAttribute('aria-disabled', String(!buyable));
        });
      }

      /**
       * A size the selected colour cannot supply is struck through and its row locked.
       * Any pieces it held are dropped rather than quoted for a variant that cannot be
       * produced.
       */
      renderSizeAvailability() {
        if (!this.sizePicker) return;

        this.sizePicker.querySelectorAll('[data-size-value]').forEach((button) => {
          const size = button.dataset.sizeValue;
          const buyable = this.variantForSize(size)?.available ?? false;

          button.classList.toggle('main-product__size--unavailable', !buyable);
          button.setAttribute('aria-disabled', String(!buyable));

          const input = this.sizeInput(size);
          if (input) {
            input.disabled = !buyable;
            input.closest('[data-size-row]')?.classList.toggle('main-product__size-row--unavailable', !buyable);
          }

          if (!buyable && this.selectedSizes.has(size)) this.removeSize(size);
        });
      }

      renderSizeDistribution() {
        if (!this.sizePicker) return;

        const { pieces, perPiece, sizesUnassigned } = window.mainProductStrings;
        const required = this.quantity;
        const assigned = this.assignedQuantity;
        const state = this.distributionState;

        this.sizePicker.querySelectorAll('[data-size-value]').forEach((button) => {
          const selected = this.selectedSizes.has(button.dataset.sizeValue);
          button.classList.toggle('main-product__size--selected', selected);
          button.setAttribute('aria-pressed', String(selected));
        });

        const panel = this.sizePicker.querySelector('[data-size-panel]');
        if (panel) panel.hidden = this.selectedSizes.size === 0;

        const requiredText = `/${pieces.replace('[count]', required)}`;
        this.sizePicker.querySelectorAll('[data-size-required]').forEach((element) => {
          element.textContent = requiredText;
        });

        // Before any piece is placed the note says how many still need a size; from
        // the first piece on, the progress bar takes over.
        const note = this.sizePicker.querySelector('[data-size-note]');
        if (note) {
          note.hidden = assigned > 0;
          note.querySelector('[data-size-note-text]').textContent = sizesUnassigned.replace('[count]', required);
        }

        const progress = this.sizePicker.querySelector('[data-size-progress]');
        if (progress) {
          progress.hidden = assigned === 0;
          progress.classList.toggle('main-product__size-progress--complete', state === 'complete');
          progress.classList.toggle('main-product__size-progress--over', state === 'over');
          progress.style.setProperty('--size-progress', String(Math.min(assigned / required, 1)));

          const bar = progress.querySelector('[data-size-progress-bar]');
          bar.setAttribute('aria-valuemax', String(required));
          bar.setAttribute('aria-valuenow', String(Math.min(assigned, required)));
          bar.setAttribute('aria-valuetext', `${assigned}/${required}`);

          progress.querySelector('[data-size-progress-label]').textContent = `${assigned}/${required}`;
        }

        // Pack-Schritte je Größe: falsche Zeilen markieren und den Grund direkt im Panel nennen
        const packErrors = this.packErrors();
        this.sizePicker.querySelectorAll('[data-size-quantity]').forEach((input) => {
          const step = this.variantForSize(input.dataset.sizeQuantity)?.quantityRule?.increment ?? 1;
          if (step > 1) input.setAttribute('step', String(step));
          const invalid = packErrors.some((e) => e.size === input.dataset.sizeQuantity);
          input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
          input.style.borderColor = invalid ? '#c0392b' : '';
        });
        if (panel) {
          let packNote = panel.querySelector('[data-size-pack-error]');
          if (!packNote) {
            packNote = document.createElement('p');
            packNote.setAttribute('data-size-pack-error', '');
            packNote.setAttribute('role', 'alert');
            packNote.style.cssText = 'color:#c0392b;font-size:13px;margin:8px 0 0;';
            panel.appendChild(packNote);
          }
          packNote.hidden = packErrors.length === 0;
          packNote.textContent = packErrors
            .map((e) => `${e.size}: nur in ${e.step}er-Schritten bestellbar (${e.step}, ${e.step * 2}, ${e.step * 3} …)`)
            .join(' · ');
        }

        const adjust = this.sizePicker.querySelector('[data-size-adjust]');
        if (!adjust) return;

        adjust.hidden = state !== 'over' || !this.variant;
        if (adjust.hidden) return;

        const unit = this.unitPrice(this.variant, assigned);
        adjust.querySelector('[data-size-adjust-quantity]').textContent = pieces.replace('[count]', assigned);
        adjust.querySelector('[data-size-adjust-unit]').textContent = perPiece.replace('[money]', this.formatMoney(unit));
        adjust.querySelector('[data-size-adjust-total]').textContent = this.formatMoney(unit * assigned);
      }

      renderOfferState() {
        const button = this.querySelector('offer-button button');
        if (button) button.disabled = !this.canRequestOffer;
      }

      renderVariantMeta() {
        // `this.variant` is null when the chosen options match no variant.
        const available = this.variant?.available ?? false;

        const selectedColor = this.querySelector('[data-selected-color]');
        if (selectedColor) selectedColor.textContent = this.selectedColor ?? '';

        const sku = this.querySelector('[data-sku]');
        if (sku) {
          const value = this.variant?.sku ?? '';
          sku.textContent = `${sku.dataset.skuLabel} ${value}`.trim();
          sku.hidden = value === '';
        }

        const availability = this.querySelector('[data-availability]');
        if (availability) {
          const { inStock, outOfStock } = window.mainProductStrings;
          availability.classList.toggle('main-product__availability--unavailable', !available);
          availability.querySelector('[data-availability-text]').textContent = available ? inStock : outOfStock;
        }
      }

      renderPriceInfo() {
        const quantity = this.quantity;
        const discount = this.discountPercent(this.variant, quantity);
        const total = this.unitPrice(this.variant, quantity) * quantity;

        const quantityEl = this.querySelector('[data-price-quantity]');
        if (quantityEl) quantityEl.textContent = window.mainProductStrings.pieces.replace('[count]', quantity);

        const discountEl = this.querySelector('[data-price-discount]');
        if (discountEl) {
          discountEl.textContent = `−${discount}%`;
          discountEl.hidden = discount <= 0;
        }

        const totalEl = this.querySelector('[data-price-total]');
        if (totalEl) totalEl.textContent = this.formatMoney(total);
      }

      /**
       * The tier rows themselves are rendered by Liquid: every quantity any variant
       * offers already exists in the DOM. This only re-prices them for the selected
       * variant, hides the ones it does not offer, and reflects the active row.
       */
      syncTierStates() {
        const container = this.querySelector('[data-quantity-tiers]');
        if (!container) return;

        const { perPiece, savesValue } = window.mainProductStrings;
        const { min = 1 } = this.variant.quantityRule ?? {};

        container.querySelectorAll('[data-tier-quantity]').forEach((tier) => {
          const quantity = Number(tier.dataset.tierQuantity);

          // A tier below this variant's minimum order quantity is not orderable.
          tier.hidden = quantity < min;

          const unit = this.unitPrice(this.variant, quantity);
          const discount = this.discountPercent(this.variant, quantity);

          tier.querySelector('[data-tier-unit]').textContent = perPiece.replace('[money]', this.formatMoney(unit));
          tier.querySelector('[data-tier-total]').textContent = this.formatMoney(unit * quantity);

          const badge = tier.querySelector('[data-tier-badge]');
          badge.querySelector('[data-tier-badge-value]').textContent = savesValue.replace('[percent]', discount);
          badge.hidden = discount <= 0;

          const active = !this.customMode && quantity === this.quantity;
          tier.classList.toggle('main-product__tier--active', active);
          tier.setAttribute('aria-pressed', active);
        });

        this.syncCustomRow();
      }

      /** The custom row only carries a number: no prices are shown in it. */
      syncCustomRow() {
        const row = this.querySelector('[data-custom-row]');
        if (!row) return;

        const rule = this.variant.quantityRule ?? {};

        row.classList.toggle('main-product__tier--active', this.customMode);

        const toggle = row.querySelector('[data-custom-toggle]');
        if (toggle) toggle.setAttribute('aria-pressed', String(this.customMode));

        // The rule travels on the input so validation reads the selected variant's.
        const input = row.querySelector('[data-custom-quantity]');
        if (input) {
          input.dataset.min = rule.min ?? 1;
          input.dataset.increment = rule.increment ?? 1;
          if (rule.max == null) delete input.dataset.max;
          else input.dataset.max = rule.max;
        }
      }

      /** No variant matches the chosen options: nothing to price, nothing to quote. */
      setUnavailable() {
        this.renderVariantMeta();
        this.renderOptionAvailability();
        this.renderSizeAvailability();
        this.renderSizeDistribution();
        this.renderOfferState();
      }

      /**
       * Slides to the first media whose alt names the active colour. The thumbs
       * slider follows through Swiper's thumbs module. A colour without media of its
       * own leaves the gallery where it is.
       */
      slideToColor() {
        const color = this.colorOf(this.variant)?.trim().toLowerCase();
        const slider = this.querySelector('.main-product__slider')?.swiper;
        if (!color || !slider) return;

        const index = Array.from(slider.slides).findIndex((slide) => slide.dataset.mediaColor === color);
        if (index === -1 || index === slider.activeIndex) return;

        slider.slideTo(index);
      }
    }
  );
}
